import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { apiBaseUrl } from '../../util/api';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
} from '../../util/types';

import { NamedLink } from '../../components';
import EditIcon from './EditIcon';
import { LISTING_TYPES, PRICE_VERIFICATION_STATUSES } from '../../util/listingTypes';

import css from './ListingPage.module.css';

const MAX_RECEIPT_SIZE = 5 * 1024 * 1024;
const ALLOWED_RECEIPT_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
const RECEIPT_UPLOAD_FALLBACK_ERROR = 'Unable to submit your receipt right now.';
const RECEIPT_UPLOAD_404_ERROR = '(404) Oops, something went wrong. Please try again.';

const formatListingPrice = listing => {
  const price = listing?.attributes?.price;
  if (price?.amount != null) {
    const amount = Number(price.amount) / 100;
    const currency = price.currency || 'USD';
    return `${currency} ${amount.toFixed(2)}`;
  }

  return '';
};

const ReceiptUploadModal = props => {
  const { listing, currentUser, config, onClose } = props;
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const publicData = listing?.attributes?.publicData || {};
  const listingId = listing?.id?.uuid || '';
  const listingTitle = listing?.attributes?.title || publicData.eventName || '';
  const userEmail = currentUser?.attributes?.email || '';
  const isSubmitting = status === 'submitting';
  const isSuccess = status === 'success';
  const isRejected = status === 'rejected';
  const canSubmit = receiptFile && confirmed && !isSubmitting;

  useEffect(() => {
    if (!receiptFile) {
      setReceiptPreviewUrl(null);
      return undefined;
    }

    const previewUrl = URL.createObjectURL(receiptFile);
    setReceiptPreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [receiptFile]);

  const onFileChange = e => {
    const file = e.target.files?.[0];

    setError(null);
    setStatus('idle');

    if (!file) {
      setReceiptFile(null);
      return;
    }

    if (!ALLOWED_RECEIPT_TYPES.includes(file.type)) {
      setReceiptFile(null);
      setError('Please upload a PNG, JPG, JPEG, or WebP image.');
      return;
    }

    if (file.size > MAX_RECEIPT_SIZE) {
      setReceiptFile(null);
      setError('Receipt image must be 5MB or smaller.');
      return;
    }

    setReceiptFile(file);
  };

  const onSubmit = async e => {
    e.preventDefault();

    if (!canSubmit) {
      return;
    }

    setStatus('submitting');
    setError(null);

    const formData = new FormData();
    formData.append('receipt', receiptFile);
    formData.append('listingId', listingId);
    formData.append('listingTitle', listingTitle);
    formData.append('listingPrice', formatListingPrice(listing));
    formData.append('eventName', publicData.eventName || listingTitle);
    formData.append('eventDate', publicData.eventDateTime || publicData.eventDate || '');
    formData.append('venueName', publicData.venueName || publicData.venue || '');
    formData.append('seatSection', publicData.seat_section || publicData.seatSection || '');
    formData.append('seatRow', publicData.seat_row || publicData.seatRow || '');
    formData.append('seat', publicData.seat || publicData.seatNumber || '');
    formData.append('userEmail', userEmail);

    try {
      const response = await fetch(
        `${apiBaseUrl(config?.marketplaceRootURL)}/api/price-verification/receipt`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        }
      );
      const data = await response.json();

      if (!response.ok) {
        if (data?.moderationRejected) {
          setStatus('rejected');
          setError(data.message);
          return;
        }
        const message = data?.message || '';
        const is404Error = response.status === 404 || /\b404\b/.test(message);
        throw new Error(
          is404Error ? RECEIPT_UPLOAD_404_ERROR : message || RECEIPT_UPLOAD_FALLBACK_ERROR
        );
      }

      setStatus('success');
    } catch (err) {
      setStatus('idle');
      setError(err?.message || RECEIPT_UPLOAD_FALLBACK_ERROR);
    }
  };

  return (
    <div className={css.receiptModalOverlay} role="presentation" onClick={onClose}>
      <div
        className={css.receiptModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="receipt-upload-title"
        onClick={e => e.stopPropagation()}
      >
        <button type="button" className={css.receiptModalClose} onClick={onClose}>
          &times;
        </button>

        {isSuccess ? (
          <div className={css.receiptResult}>
            <h2 id="receipt-upload-title" className={css.receiptModalTitle}>
              We&apos;re on it!
            </h2>
            <p className={css.receiptModalCopy}>
              We&apos;ll review your receipt shortly. Once we verify your price is not higher than
              what you paid, we&apos;ll send a friendly email to let you know.
            </p>
            <button type="button" className={css.receiptSubmitButton} onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form className={css.receiptForm} onSubmit={onSubmit}>
            <h2 id="receipt-upload-title" className={css.receiptModalTitle}>
              Upload your receipt
            </h2>
            <p className={css.receiptModalCopy}>
              To help us confirm your listing price, please send a screenshot of your ticket
              receipt. Make sure it includes the event name, seat details and amount paid for your
              ticket(s).
            </p>

            <div className={css.receiptUploadLabel}>Screenshot of receipt</div>
            <div className={css.receiptUploadBox}>
              <div className={css.receiptUploadRow}>
                <label className={css.receiptUploadButton} htmlFor="receipt-upload-input">
                  Choose file
                </label>
                <input
                  id="receipt-upload-input"
                  className={css.receiptUploadInput}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={onFileChange}
                />
                <span className={css.receiptUploadFileName}>
                  {receiptFile ? receiptFile.name : 'Pending upload...'}
                </span>
              </div>
              {receiptPreviewUrl ? (
                <div className={css.receiptPreviewFrame}>
                  <img
                    className={css.receiptPreviewImage}
                    src={receiptPreviewUrl}
                    alt={`Receipt preview for ${receiptFile.name}`}
                  />
                </div>
              ) : null}
            </div>
            <p className={css.receiptUploadHint}>Allowed formats: PNG, JPG, JPEG, WebP. Max 5MB.</p>

            <label className={css.receiptCheckboxRow}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
              />
              <span>
                Please avoid sharing passwords, credit card details or other sensitive information.
              </span>
            </label>

            {error ? (
              <p
                className={classNames(css.receiptModalError, { [css.receiptRejected]: isRejected })}
              >
                {error}
              </p>
            ) : null}

            <div className={css.receiptModalActions}>
              <button type="button" className={css.receiptCancelButton} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className={css.receiptSubmitButton} disabled={!canSubmit}>
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ReceiptUploadButtonMaybe = props => {
  const { listing, currentUser, config, isEnabled } = props;
  const [isOpen, setIsOpen] = useState(false);

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <button type="button" className={css.actionBarCTA} onClick={() => setIsOpen(true)}>
        Upload receipt
      </button>
      {isOpen ? (
        <ReceiptUploadModal
          listing={listing}
          currentUser={currentUser}
          config={config}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

/**
 * The ActionBarMaybe component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.isOwnListing - Whether the listing is own
 * @param {propTypes.listing | propTypes.ownListing} props.listing - The listing
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.editParams - The path params for the named route to edit the listing
 * @param {string} props.editParams.id - The id
 * @param {string} props.editParams.slug - The slug
 * @param {'edit' | 'draft'} props.editParams.type - The type
 * @param {string} props.editParams.tab - The tab (e.g. 'details' or 'pricing')
 * @param {boolean} props.showNoPayoutDetailsSet - Show info about missing payout details
 * @returns {JSX.Element} action bar maybe component
 */
export const ActionBarMaybe = props => {
  const {
    rootClassName,
    className,
    isOwnListing,
    listing,
    currentUser,
    editParams,
    showNoPayoutDetailsSet,
    showBidOfferPayoutWarning,
  } = props;
  const classes = classNames(rootClassName || css.actionBar, className);

  const state = listing.attributes.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;

  const config = useConfiguration();

  if (showBidOfferPayoutWarning) {
    return (
      <div className={classes}>
        <p className={classNames(css.ownListingText, css.missingPayoutDetailsText)}>
          <FormattedMessage id="ListingPage.bidOfferPayoutWarningMessage" />
        </p>
        <NamedLink className={css.addPayoutDetails} name="StripePayoutPage">
          <EditIcon className={css.editIcon} />
          <FormattedMessage id="ListingPage.addPayoutDetails" />
        </NamedLink>
      </div>
    );
  } else if (isOwnListing && showNoPayoutDetailsSet) {
    return (
      <div className={classes}>
        <p className={classNames(css.ownListingText, css.missingPayoutDetailsText)}>
          <FormattedMessage id="ListingPage.addPayoutDetailsMessage" />
        </p>
        <NamedLink className={css.addPayoutDetails} name="StripePayoutPage">
          <EditIcon className={css.editIcon} />
          <FormattedMessage id="ListingPage.addPayoutDetails" />
        </NamedLink>
      </div>
    );
  } else if (isOwnListing) {
    // Check if this is a bid listing (testing-bids). Be defensive:
    // during first render some listing fields can be missing/lagging.
    const publicData = listing?.attributes?.publicData || {};
    const listingType = publicData.listingType;
    const transactionProcessAlias = publicData.transactionProcessAlias || '';
    const isDefaultNegotiationProcess =
      typeof transactionProcessAlias === 'string' &&
      transactionProcessAlias.startsWith('default-negotiation');
    const isBidListing =
      listingType === LISTING_TYPES.TICKET_REQUEST || isDefaultNegotiationProcess;
    const isTicketSaleListing = listingType === LISTING_TYPES.TICKET_SALE;
    const priceVerificationStatus =
      publicData.price_verification_status || PRICE_VERIFICATION_STATUSES.UNVERIFIED;
    const shouldPromptForPriceVerification =
      isTicketSaleListing &&
      !isDraft &&
      !isClosed &&
      priceVerificationStatus === PRICE_VERIFICATION_STATUSES.UNVERIFIED;

    let ownListingTextTranslationId = shouldPromptForPriceVerification
      ? 'ListingPage.ownListingUnverifiedPrice'
      : isBidListing
      ? 'ListingPage.ownBid'
      : 'ListingPage.ownListing';

    if (isPendingApproval) {
      // Use different message for bid listings
      ownListingTextTranslationId = isBidListing
        ? 'ListingPage.ownBidPendingApproval'
        : 'ListingPage.ownListingPendingApproval';
    } else if (isClosed) {
      ownListingTextTranslationId = isBidListing
        ? 'ListingPage.ownClosedBid'
        : 'ListingPage.ownClosedListing';
    } else if (isDraft) {
      ownListingTextTranslationId = isBidListing
        ? 'ListingPage.ownBidDraft'
        : 'ListingPage.ownListingDraft';
    }

    // Use different button text for bid listings
    const message = isDraft
      ? 'ListingPage.finishListing'
      : isBidListing
      ? 'ListingPage.editBid'
      : 'ListingPage.editListing';

    const ownListingTextClasses = classNames(css.ownListingText, {
      [css.ownListingTextPendingApproval]: isPendingApproval,
    });

    const isCTAEnabled = shouldPromptForPriceVerification && !isBidListing;

    return (
      <div
        className={classNames(classes, {
          [css.actionBarWithCTAEnabled]: isCTAEnabled,
        })}
      >
        <p className={classNames(ownListingTextClasses, { [css.CTAEnabled]: isCTAEnabled })}>
          <FormattedMessage id={ownListingTextTranslationId} />
        </p>
        <span className={isCTAEnabled ? css.linkContainer : css.noShrink}>
          <NamedLink
            className={classNames(css.editListingLink, { [css.CTAEnabled]: isCTAEnabled })}
            name="EditListingPage"
            params={editParams}
          >
            <EditIcon className={css.editIcon} />
            <FormattedMessage id={message} />
          </NamedLink>
          {/* Only render CTA button (Upload receipt) for non-bid listings */}
          {!isBidListing ? (
            <ReceiptUploadButtonMaybe
              listing={listing}
              currentUser={currentUser}
              config={config}
              isEnabled={shouldPromptForPriceVerification}
            />
          ) : null}
        </span>
      </div>
    );
  } else if (isClosed) {
    return (
      <div className={classes}>
        <p className={css.closedListingText}>
          <FormattedMessage id="ListingPage.closedListing" />
        </p>
      </div>
    );
  }
  return null;
};

export default ActionBarMaybe;
